import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { initialState, reducer } from 'features/competenceOrder/reducers/rules/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import { competenceOrderRuleValidator } from 'features/competenceOrder/validators';
import CompetenceOrderRuleListCard from './CompetenceOrderRuleListCard';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { useApolloClient } from '@apollo/client';
import { RULE_STATUS_QUERY } from '../../../queries';
import { COMPETENCE_ORDERS_RULE_SAVE, COMPETENCE_ORDERS_RULE_DELETE } from '../../../mutations';
import { COMPETENCE_ORDER_RULE_DELETE_SUBSCRIPTION } from '../../../subscriptions';
const CompetenceOrderRuleListContainer = ({ data, competenceOrderId, siteId, approvalTypeLevels }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const client = useApolloClient();

  const [localState, dispatch] = useReducer(reducer, initialState);

  const [competenceOrderRulesValidation, validateCompetenceOrderRules] = useValidation(competenceOrderRuleValidator);

  // #region Queries

  const { data: ruleStatusesData } = useQuery(RULE_STATUS_QUERY);

  // #endregion

  // #region Mutations

  const [competenceOrderRuleSave] = useMutation(COMPETENCE_ORDERS_RULE_SAVE);

  const [competenceOrderRuleDelete] = useMutation(COMPETENCE_ORDERS_RULE_DELETE);

  // #endregion

  // #region Subscriptions

  useSubscription(COMPETENCE_ORDER_RULE_DELETE_SUBSCRIPTION, {
    onData: ({ _, client }) => {
      addToast(t('CompetenceOrders.DeletedCompetenceOrderRule'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'competenceOrder' });
      client.cache.gc();
    }
  });

  // #endregion

  // #region Handlers

  const handleAdd = () => {
    dispatch({ type: 'OnRuleAdded' });
  };

  const handleDelete = index => () => {
    if (window.confirm(t('Tooltips.CompetenceOrder.DeleteRule'))) {
      if (localState.rules[index].competenceOrderRuleId !== null) {
        const input = { competenceOrderRuleId: localState.rules[index].competenceOrderRuleId };
        dispatch({ type: 'OnRuleDeleted', index });
        return competenceOrderRuleDelete({ variables: { input } });
      } else {
        dispatch({ type: 'OnRuleDeleted', index });
      }
    }
  };

  const handleSave = async idx => {
    // eslint-disable-next-line no-unused-vars
    const { changed, isExpanded, ...rule } = localState.rules[idx];

    const result = await competenceOrderRuleSave({ variables: { input: { ...rule } } });

    client.cache.evict({
      id: `CompetenceOrder:{"competenceOrderId":${result.data.competenceOrderRuleSave.competenceOrderId}}`
    });
    client.cache.gc();

    dispatch({ type: 'OnRuleSaved', data: { idx: idx, result } });
    addToast(t('CompetenceOrders.AddedOrUpdatedCompetenceOrderRule'), 'success');
  };

  const handlePropertyChanged = (detailPropertyId, detailPropertyValue, index) => {
    let payload;
    switch (detailPropertyId) {
      case 'approvalTypeLevelId':
        payload = {
          detailPropertyId,
          detailPropertyValue,
          detailPropertyName: 'levelName',
          detailPropertyNameValue: approvalTypeLevels.find(x => x.approvalTypeLevelId === detailPropertyValue).levelName,
          index
        };
        break;
      case 'ruleStatusId':
        payload = {
          detailPropertyId,
          detailPropertyValue,
          detailPropertyName: 'ruleStatusName',
          detailPropertyNameValue: ruleStatusesData?.ruleStatus.find(x => x.statusId === detailPropertyValue).statusName,
          index
        };
        break;
      default:
        payload = { detailPropertyId, detailPropertyValue, index };
        break;
    }

    dispatch({ type: 'OnDetailsPropertyChanged', payload });
  };

  const handleExpandChange = (isExpanded, index) => {
    dispatch({ type: 'OnRuleExpanded', index, isExpanded });
  };

  // #endregion

  // #region Side effects

  useEffect(() => {
    if (data && localState === initialState) dispatch({ type: 'OnInitState', data: { data, competenceOrderId, siteId } });
  }, [competenceOrderId, data, localState, siteId]);

  useEffect(() => {
    validateCompetenceOrderRules(localState.rules);
  }, [localState.rules, validateCompetenceOrderRules]);

  // #endregion

  return (
    <CompetenceOrderRuleListCard
      competenceOrderRules={localState.rules}
      approvalTypeLevels={approvalTypeLevels}
      ruleStatuses={ruleStatusesData?.ruleStatus || []}
      onSave={handleSave}
      onAdd={handleAdd}
      onPropertyChanged={handlePropertyChanged}
      onDelete={handleDelete}
      onExpandChange={handleExpandChange}
      validation={competenceOrderRulesValidation}
    />
  );
};

CompetenceOrderRuleListContainer.propTypes = {
  data: PropTypes.array.isRequired,
  competenceOrderId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  approvalTypeLevels: PropTypes.array.isRequired
};

export default CompetenceOrderRuleListContainer;
